import { UserRole } from "@jugl-web/rest-api";
import { PreviewTask } from "@jugl-web/rest-api/tasks";
import { Button } from "@jugl-web/ui-components/cross-platform/Button";
import { SearchInput } from "@jugl-web/ui-components/cross-platform/SearchInput";
import { cx, useSearchInput, useTranslations } from "@jugl-web/utils";
import { FC, useMemo, useState } from "react";
import { VList } from "virtua";
import { useTaskBoardList } from "../../hooks/useTaskBoardList";
import { useTaskBoardsQuery } from "../../hooks/useTaskBoardsQuery";
import { useTaskModulePermissions } from "../../hooks/useTaskModulePermissions";
import {
  TaskPermissions,
  useTaskPermissionsFactory,
} from "../../hooks/useTaskPermissions";
import { useTasks } from "../../hooks/useTasks";
import { ReactComponent as PlusIcon } from "./assets/plus.svg";
import { BoardPill } from "./components/BoardPill";
import { CreateTaskListItem } from "./components/CreateTaskListItem";
import { TaskListItem } from "./components/TaskListItem";

export interface SelectTaskListProps {
  entityId: string;
  meId: string;
  /** Optional board ID. When provided, runs in board-scoped mode without board filtering pills */
  boardId?: string | null;
  /** Optional user role. Needs to be provided if "Create New Task" button is visible */
  userRole?: UserRole;
  /** Currently selected task ID */
  selectedTaskId?: string | null;
  /** Optional filter function to determine which tasks should be filtered out */
  filterTaskFn?: (
    task: PreviewTask,
    taskPermissions: TaskPermissions
  ) => boolean;
  /** Optional callback to create a new task */
  onRequestCreateTask?: () => void;
  /** Callback when a task is selected */
  onSelect: (targetTask: PreviewTask) => void;
}

export const SelectTaskList: FC<SelectTaskListProps> = ({
  entityId,
  meId,
  boardId,
  userRole,
  selectedTaskId,
  filterTaskFn,
  onRequestCreateTask,
  onSelect,
}) => {
  const { getBoardByIdFromCache } = useTaskBoardsQuery({ entityId });

  const { accessibleBoards } = useTaskBoardList({ entityId, meId });

  const hasBoardFiltering = Boolean(boardId);

  const [selectedTaskBoardId, setSelectedTaskBoardId] = useState("my");

  const { searchQuery, inputProps, reset } = useSearchInput({
    debounce: 500,
  });

  const { t } = useTranslations();

  const { canCreateTask } = useTaskModulePermissions({
    entityId,
    meId,
    boardId,
    userRole,
  });

  // Use provided board ID if in scoped mode, otherwise use selected board
  const { tasks } = useTasks({
    meId,
    entityId,
    source: { type: "boardTasks", boardId: boardId || selectedTaskBoardId },
    searchQuery,
  });

  const taskBoardItems = useMemo(
    () => [
      {
        id: "my",
        title: t({
          id: "home-sidebar-component.my-tasks",
          defaultMessage: "My Tasks",
        }),
      },
      ...accessibleBoards.map((board) => ({
        id: board.id,
        title: board.name,
      })),
    ],
    [t, accessibleBoards]
  );

  const getTaskPermissions = useTaskPermissionsFactory({ entityId, meId });

  const filteredTasks = useMemo(() => {
    if (!filterTaskFn) {
      return tasks;
    }

    return tasks.filter((task) => {
      const taskPermissions = getTaskPermissions(task);
      return filterTaskFn(task, taskPermissions);
    });
  }, [filterTaskFn, tasks, getTaskPermissions]);

  return (
    <div className="p-3">
      <SearchInput
        variant="filled"
        color="grey"
        {...inputProps}
        onClear={reset}
      />
      {/* Only show board filtering pills when NOT in scoped mode */}
      {!hasBoardFiltering && (
        <div className="jugl__custom-scrollbar-horizontal mt-2 flex gap-2 overflow-x-auto py-1.5">
          {taskBoardItems.map((board) => {
            const isSelected = selectedTaskBoardId === board.id;

            return (
              <BoardPill
                key={board.id}
                title={board.title}
                isSelected={isSelected}
                onClick={() => setSelectedTaskBoardId(board.id)}
              />
            );
          })}
        </div>
      )}
      <div
        className={cx(
          "flex h-[320px] flex-col",
          hasBoardFiltering ? "mt-3" : "mt-5"
        )}
      >
        {filteredTasks.length > 0 ? (
          <VList className="jugl__custom-scrollbar flex-1">
            {canCreateTask && onRequestCreateTask && (
              <CreateTaskListItem onClick={() => onRequestCreateTask()} />
            )}
            {filteredTasks.map((task) => (
              <TaskListItem
                key={task.id}
                task={task}
                searchQuery={searchQuery}
                boardName={
                  task.board_id
                    ? getBoardByIdFromCache(task.board_id)?.name || "No Board"
                    : "No Board"
                }
                isSelected={selectedTaskId === task.id}
                onClick={() => onSelect(task)}
              />
            ))}
          </VList>
        ) : searchQuery.length > 0 ? (
          <NoResultsEmptyState />
        ) : (
          <NoTaskCreatedEmptyState
            onRequestCreateTask={
              canCreateTask ? onRequestCreateTask : undefined
            }
          />
        )}
      </div>
    </div>
  );
};

const NoResultsEmptyState = () => {
  const { t } = useTranslations();

  return (
    <div className="my-4 text-center">
      <span className="font-secondary text-sm leading-[160%] text-[#828282]">
        {t({
          id: "tasks-page.no-results",
          defaultMessage: "No results 😔",
        })}
      </span>
    </div>
  );
};

interface NoTaskCreatedEmptyStateProps {
  /** If not provided, the "Create new Task" button will not be shown */
  onRequestCreateTask?: () => void;
}

const NoTaskCreatedEmptyState: FC<NoTaskCreatedEmptyStateProps> = ({
  onRequestCreateTask,
}) => {
  const { t } = useTranslations();

  return (
    <div className="font-secondary flex h-[300px] flex-col items-center justify-center gap-2 text-center">
      <span className="text-dark text-center text-base font-medium">
        {t({
          id: "tasks-page.no-task-found-title",
          defaultMessage: "No Task found",
        })}
      </span>
      <span className="text-sm text-[#828282]">
        {t({
          id: "tasks-page.no-tasks-found-description",
          defaultMessage: "Board doesn't have any Tasks available yet",
        })}
      </span>
      {onRequestCreateTask && (
        <Button
          className="mt-4 h-10 px-8 font-medium"
          onClick={() => onRequestCreateTask?.()}
        >
          {t({
            id: "tasks-page.create-new-task",
            defaultMessage: "Create new Task",
          })}
          <PlusIcon />
        </Button>
      )}
    </div>
  );
};
